// components/HomeSummaryPanel.js
// Replaces CombinedInsights.js on the patient homepage. For the logged-in
// patient's own identity, or any telehealth kid linked to their account
// (via /me/patients), shows every report type's dials in a compact grid
// plus a short, collapsible AI summary — sourced from /me/homepage-summary,
// which merges real proovereport generation and free-converted reports
// (real always wins per report type) and caches the condensed bullets
// server-side until the underlying data actually changes.
import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Box, Typography, Autocomplete, TextField, CircularProgress, Collapse, IconButton, Chip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import ChildCareRoundedIcon from "@mui/icons-material/ChildCareRounded";
import UserContext from "./UserContext";
import ScoreDial from "./reports/ScoreDial";
import { levelColor, levelBg, gradeColor } from "./reports/reportHelpers";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const BRAND = { navy: "#0a0e27", navyMid: "#1a2244", accent: "#637cdb" };

const REPORT_TYPE_LABELS = {
  opioid_risk: "Opioid Risk",
  pain_perception: "Pain Perception",
  pgx: "Drug Metabolism",
};
const STAT_LABELS = {
  proove_ori: "Proove Opioid Risk",
  ort: "Opioid Risk Tool (ORT)",
  phq2: "Personal Health Questionnaire (PHQ-2)",
  self_reported_pain: "Self-Reported Pain",
  pss: "Perceived Stress Scale (PSS)",
  comt_predisposition: "Genetic Predisposition (COMT)",
};

function patientOptionLabel(p) {
  const name = `${p.first_name || ""} ${p.last_name || ""}`.trim() || p.label || "Patient";
  return p.date_of_birth ? `${name} (${p.date_of_birth})` : name;
}

function MiniDial({ label, stat, isDark, index }) {
  if (!stat || (stat.level == null && stat.score == null)) return null;
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.35, delay: index * 0.05, ease: "easeOut" }}
      style={{ flex: "1 1 160px", minWidth: 150, maxWidth: 200 }}
    >
      <Box sx={{
        borderRadius: "14px", p: 1.75, textAlign: "center", height: "100%",
        bgcolor: isDark ? "rgba(255,255,255,0.03)" : "#fff",
        border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(10,14,39,0.07)"}`,
      }}>
        {/* Wraps instead of truncating — some stat names ("Personal Health
            Questionnaire (PHQ-2)") are long enough that ellipsis-truncation
            made them unreadable in a card this narrow. A short two-line
            label reads better than a cut-off one-liner. */}
        <Typography sx={{
          fontSize: 10, fontWeight: 700, color: isDark ? "#94a3b8" : "#64748b",
          mb: 0.75, textTransform: "uppercase", letterSpacing: "0.03em",
          wordBreak: "break-word", overflowWrap: "break-word", lineHeight: 1.35,
          minHeight: "2.7em", display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          {label}
        </Typography>
        <Box sx={{ display: "flex", justifyContent: "center", transform: "scale(0.82)" }}>
          <ScoreDial level={stat.level} score={stat.score} />
        </Box>
        <Box sx={{
          mt: 0.5, display: "inline-block", borderRadius: 999, px: 1.25, py: 0.3,
          fontSize: 10.5, fontWeight: 700, color: levelColor(stat.level), bgcolor: levelBg(stat.level),
        }}>
          {stat.level || "N/A"}
        </Box>
      </Box>
    </motion.div>
  );
}

export default function HomeSummaryPanel({ isDark }) {
  const theme = useTheme();
  const { user } = useContext(UserContext);
  const token = user?.token;
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [patients, setPatients] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loadingPatients, setLoadingPatients] = useState(true);
  const [summary, setSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [expanded, setExpanded] = useState(true);

  const fetchSummary = (patient) =>
    axios.get(`${API_BASE}/me/homepage-summary`, {
      headers,
      params: { name_hash: patient.name_hash || "", dob_hash: patient.dob_hash || "" },
    }).then((res) => res.data);

  // Initial load: fetch the family list, then probe each identity in
  // order (self first, then each kid) until one actually has reports —
  // "if the account owner has none of their own, default to the first kid
  // who does." Falls back to self (showing "no reports yet") if nobody has
  // anything. The winning probe's result is reused directly so this
  // doesn't double-fetch the same identity a second time afterward.
  useEffect(() => {
    if (!token) return;
    let mounted = true;
    axios.get(`${API_BASE}/me/patients`, { headers })
      .then(async (res) => {
        if (!mounted) return;
        const list = res.data?.patients || [];
        setPatients(list);
        if (list.length === 0) return;

        for (const patient of list) {
          let data;
          try {
            data = await fetchSummary(patient);
          } catch {
            continue;
          }
          if (!mounted) return;
          if ((data.report_types || []).length > 0) {
            setSelected(patient);
            setSummary(data);
            return;
          }
        }
        // Nobody has reports yet — default to self, empty state.
        if (mounted) { setSelected(list[0]); setSummary({ report_types: [] }); }
      })
      .catch(() => { if (mounted) setPatients([]); })
      .finally(() => { if (mounted) setLoadingPatients(false); });
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Only re-fetches when the user manually picks a DIFFERENT patient from
  // the dropdown — the initial auto-selected one is already loaded above.
  const initialLoadDone = React.useRef(false);
  useEffect(() => {
    if (!token || !selected) return;
    if (!initialLoadDone.current) { initialLoadDone.current = true; return; }
    let mounted = true;
    setLoadingSummary(true);
    fetchSummary(selected)
      .then((data) => { if (mounted) setSummary(data); })
      .catch(() => { if (mounted) setSummary(null); })
      .finally(() => { if (mounted) setLoadingSummary(false); });
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.name_hash, selected?.dob_hash]);

  if (loadingPatients) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress size={22} sx={{ color: BRAND.accent }} />
      </Box>
    );
  }
  if (patients.length === 0) return null;

  const border = isDark ? "rgba(255,255,255,0.08)" : "rgba(10,14,39,0.08)";
  const cardBg = isDark ? "rgba(17,24,39,0.62)" : "rgba(255,255,255,0.72)";
  const hasReports = summary && (summary.report_types || []).length > 0;

  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: "easeOut" }}>
      <Box sx={{
        borderRadius: "20px", overflow: "hidden", mb: 4,
        bgcolor: cardBg, backdropFilter: "blur(20px) saturate(140%)",
        WebkitBackdropFilter: "blur(20px) saturate(140%)",
        border: `1px solid ${border}`,
        boxShadow: isDark ? "0 20px 60px rgba(0,0,0,0.35)" : "0 20px 60px rgba(10,14,39,0.07)",
      }}>
        {/* Header — patient picker */}
        <Box sx={{
          display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap",
          px: { xs: 2.5, sm: 3 }, py: 2.25,
          background: `linear-gradient(135deg, ${BRAND.navy} 0%, ${BRAND.navyMid} 100%)`,
        }}>
          <Box sx={{
            width: 38, height: 38, borderRadius: "11px", flexShrink: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
            bgcolor: "rgba(99,124,219,0.18)", border: "1px solid rgba(99,124,219,0.3)",
          }}>
            <AutoAwesomeRoundedIcon sx={{ color: "#a8b6f0", fontSize: 19 }} />
          </Box>

          {patients.length > 1 ? (
            <Autocomplete
              size="small"
              sx={{ minWidth: 240, flex: "0 1 320px" }}
              options={patients}
              value={selected}
              onChange={(_, value) => setSelected(value)}
              getOptionLabel={patientOptionLabel}
              isOptionEqualToValue={(a, b) => a.name_hash === b.name_hash && a.dob_hash === b.dob_hash}
              disableClearable
              renderOption={(props, p) => (
                <Box component="li" {...props} key={`${p.name_hash}:${p.dob_hash}`} sx={{ display: "flex !important", alignItems: "center", gap: 1 }}>
                  {p.is_self ? <PersonRoundedIcon sx={{ fontSize: 16, color: "text.secondary" }} /> : <ChildCareRoundedIcon sx={{ fontSize: 16, color: "text.secondary" }} />}
                  <Typography sx={{ fontSize: 14 }}>{patientOptionLabel(p)}</Typography>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="filled"
                  InputProps={{
                    ...params.InputProps, disableUnderline: true,
                    sx: {
                      bgcolor: "rgba(255,255,255,0.08)", borderRadius: "10px",
                      color: "#fff", fontSize: 13.5, "& input": { color: "#fff" },
                      "&:hover": { bgcolor: "rgba(255,255,255,0.12)" },
                    },
                  }}
                  sx={{ "& .MuiFilledInput-input": { py: "9px !important" } }}
                />
              )}
            />
          ) : (
            <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#fff" }}>
              {patientOptionLabel(patients[0])}
            </Typography>
          )}

          <Box sx={{ flex: 1 }} />
          {hasReports && (
            <IconButton size="small" onClick={() => setExpanded((e) => !e)} sx={{ color: "#a8b6f0" }}>
              <motion.div animate={{ rotate: expanded ? 180 : 0 }} transition={{ duration: 0.25 }}>
                <ExpandMoreRoundedIcon />
              </motion.div>
            </IconButton>
          )}
        </Box>

        {/* Body */}
        <Box sx={{ p: { xs: 2.5, sm: 3 } }}>
          {loadingSummary ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={20} sx={{ color: BRAND.accent }} />
            </Box>
          ) : !hasReports ? (
            <Typography sx={{ fontSize: 13.5, color: isDark ? "#94a3b8" : "#64748b", textAlign: "center", py: 2 }}>
              No reports yet for {selected?.is_self ? "your account" : (selected?.first_name || "this patient")}.
            </Typography>
          ) : (
            <>
              {/* Dials — segregated into one labeled group per report type,
                  each group laid out as a grid (never stacked vertically),
                  so this section stays compact and scannable regardless of
                  how many report types exist. */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2.25, mb: summary.bullets_html ? 2.5 : 0 }}>
                {(summary.report_types || []).map((rt, ti) => (
                  <Box key={rt}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.25 }}>
                      <Chip
                        size="small"
                        label={REPORT_TYPE_LABELS[rt] || rt}
                        sx={{
                          bgcolor: isDark ? "rgba(99,124,219,0.15)" : "rgba(99,124,219,0.1)",
                          color: BRAND.accent, fontWeight: 700, fontSize: 11,
                          letterSpacing: "0.03em", height: 22,
                        }}
                      />
                      <Box sx={{ flex: 1, height: 1, bgcolor: isDark ? "rgba(255,255,255,0.06)" : "rgba(10,14,39,0.06)" }} />
                    </Box>
                    {rt === "pgx" && (summary.tables?.[rt] || []).length > 0 ? (
                      // Drug Metabolism has no single numeric score to put
                      // on a dial — shows its actual extracted table instead
                      // (either "findings", the old-report-conversion shape,
                      // or "genes", the current-format-report shape).
                      <Box sx={{ overflowX: "auto" }}>
                        <Box component="table" sx={{
                          width: "100%", borderCollapse: "collapse", fontSize: 12,
                          "& th": {
                            textAlign: "left", padding: "4px 8px", fontWeight: 700,
                            color: isDark ? "#94a3b8" : "#64748b", fontSize: 10.5,
                            textTransform: "uppercase", letterSpacing: "0.03em",
                          },
                          "& td": {
                            padding: "4px 8px", borderTop: isDark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(10,14,39,0.06)",
                            color: isDark ? "#e2e8f0" : "#1e293b", verticalAlign: "top",
                          },
                        }}>
                          <thead>
                            <tr>
                              {(summary.tables[rt][0]?.enzyme !== undefined
                                ? ["Enzyme", "Grade", "Medications"]
                                : ["Gene", "Genotype", "Phenotype"]
                              ).map((h) => <th key={h}>{h}</th>)}
                            </tr>
                          </thead>
                          <tbody>
                            {summary.tables[rt].map((row, i) => (
                              <tr key={i}>
                                {row.enzyme !== undefined ? (
                                  <>
                                    <td style={{ fontWeight: 600 }}>{row.enzyme}</td>
                                    <td>
                                      {row.grade ? (
                                        <Box sx={{
                                          display: "inline-flex", alignItems: "center", gap: 0.75,
                                          borderRadius: 999, px: 1, py: 0.25,
                                          bgcolor: gradeColor(row.grade).bg, color: gradeColor(row.grade).fg,
                                          fontWeight: 700, fontSize: 11, whiteSpace: "nowrap",
                                        }}>
                                          <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: gradeColor(row.grade).fg, flexShrink: 0 }} />
                                          {row.grade}
                                        </Box>
                                      ) : "—"}
                                    </td>
                                    <td>{row.medications || "—"}</td>
                                  </>
                                ) : (
                                  <>
                                    <td style={{ fontWeight: 600 }}>{row.gene}</td>
                                    <td>{row.genotype || "—"}</td>
                                    <td>{row.phenotype || "—"}</td>
                                  </>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </Box>
                      </Box>
                    ) : (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                        {Object.entries(summary.stats?.[rt] || {}).map(([key, stat], i) => (
                          <MiniDial
                            key={`${rt}:${key}`} isDark={isDark} index={ti * 3 + i}
                            label={STAT_LABELS[key] || key}
                            stat={stat}
                          />
                        ))}
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>

              {/* Collapsible AI summary */}
              {summary.bullets_html && (
                <AnimatePresence initial={false}>
                  {expanded && (
                    <motion.div
                      key="bullets"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: "easeInOut" }}
                      style={{ overflow: "hidden" }}
                    >
                      <Box sx={{
                        borderRadius: "14px", p: 2,
                        bgcolor: isDark ? "rgba(99,124,219,0.06)" : "rgba(99,124,219,0.05)",
                        border: `1px solid rgba(99,124,219,0.18)`,
                      }}>
                        <Typography sx={{
                          fontSize: 10.5, fontWeight: 800, letterSpacing: "0.1em", color: BRAND.accent,
                          mb: 1, textTransform: "uppercase",
                        }}>
                          AI Summary
                        </Typography>
                        <Box
                          sx={{
                            fontSize: 13, lineHeight: 1.9, color: isDark ? "#e2e8f0" : "#1e293b",
                            "& ul": { m: 0, pl: 2.5 },
                            "& li": { mb: 0.5 },
                          }}
                          dangerouslySetInnerHTML={{ __html: summary.bullets_html }}
                        />
                      </Box>
                    </motion.div>
                  )}
                </AnimatePresence>
              )}

              {/* One shared disclaimer for the whole panel — matches the old
                  Proove "Summary View" page's own convention (one FDA-clearance
                  note at the bottom, not repeated per report-type section). */}
              <Typography sx={{ mt: 2.5, fontSize: 10, lineHeight: 1.6, color: isDark ? "#94a3b8" : "#64748b", opacity: 0.85 }}>
                These test(s) have not been cleared or approved by the FDA; such clearance is not required.
                Results are not intended as the sole means for clinical diagnosis or patient management —
                please discuss with a licensed healthcare provider.
              </Typography>
            </>
          )}
        </Box>
      </Box>
    </motion.div>
  );
}
